import type { User } from "@/types";

/**
 * Every role name the backend can return. Mirrors UserRole in
 * app/models/user.py and the Postgres `user_role` enum.
 */
export type Role = "buyer" | "seller" | "bl_advisor" | "admin" | "org_admin";

/**
 * Both tiers that reach the admin console.
 *
 * Spread into route guards — `roles={[...ADMIN_ROLES]}` — and into role checks
 * — `hasRole(...ADMIN_ROLES)`. Adding a future admin-ish role is then one edit
 * here instead of a sweep through sixty call sites.
 */
export const ADMIN_ROLES: Role[] = ["admin", "org_admin"];

/**
 * Platform-wide surfaces an organization's admin must never reach:
 * announcements, release notes, NDA management. Deliberately NOT ADMIN_ROLES —
 * these are the pages whose content every organization's users see.
 */
export const PLATFORM_ADMIN_ROLES: Role[] = ["admin"];

const has = (user: User | null | undefined, role: Role): boolean =>
  Boolean(user?.roles?.includes(role));

/** BusinessLocating's own admin — unscoped, sees and manages everything. */
export const isPlatformAdmin = (user?: User | null): boolean => has(user, "admin");

/** A tenant's admin — scoped to their own organization's data and listings. */
export const isOrgAdmin = (user?: User | null): boolean =>
  has(user, "org_admin") && !has(user, "admin");

/** Either admin tier. Use for "can see the admin console" style checks. */
export const isAnyAdmin = (user?: User | null): boolean =>
  has(user, "admin") || has(user, "org_admin");

/**
 * Whether this user may act on a listing administered by `listingOrgId`.
 *
 * Presentation only — it decides whether a button is disabled. The API returns
 * 403 regardless, and that is the enforcement.
 */
export const canActOnListingOrg = (
  user: User | null | undefined,
  listingOrgId: number | null | undefined,
): boolean => {
  if (isPlatformAdmin(user)) return true;
  if (isOrgAdmin(user)) return listingOrgId === user?.org_id;
  return false;
};
